import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import moment from "moment";
import { ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { UseQueryResult } from "react-query";
import styled from "styled-components";
import { TContractEmployeeValuationHistoryItem } from "../../../../../../api/contract/apiListContractEmployeeValuationHistory";
import cuideoTheme from "../../../../../../containers/themes/defaultTheme";

const StyledContractName = styled(Typography)`
  color: ${cuideoTheme.palette.primary.main};
  font-size: 0.875rem;
  margin: 0 0 1rem;
`;

const StyledLoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 4rem;
`;

const StyledEmptyText = styled(Typography)`
  font-size: 0.875rem;
  text-align: center;
`;

const StyledHeadCell = styled(TableCell)`
  color: ${cuideoTheme.palette.primary.light};
  font-size: 0.75rem;
  font-weight: 700;
`;

const StyledBodyCell = styled(TableCell)`
  color: ${cuideoTheme.palette.primary.main};
  font-size: 0.875rem;
  white-space: pre-line;
`;

type TContractFieldHistoryDialogProps = {
  open: boolean;
  dialogId: string;
  contractName?: string;
  title: ReactNode;
  emptyText: ReactNode;
  errorText: ReactNode;
  historyQuery: UseQueryResult<TContractEmployeeValuationHistoryItem[]>;
  onClose: () => void;
};

/**
 * History of the editions of a contract field: date, user, old and new value.
 */
const ContractFieldHistoryDialog = ({
  open,
  dialogId,
  contractName,
  title,
  emptyText,
  errorText,
  historyQuery,
  onClose,
}: TContractFieldHistoryDialogProps) => {
  const titleId = `${dialogId}-title`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby={titleId}
    >
      <DialogTitle id={titleId}>{title}</DialogTitle>
      <DialogContent>
        {contractName && (
          <StyledContractName>{contractName}</StyledContractName>
        )}
        {historyQuery.isLoading && (
          <StyledLoadingWrapper>
            <CircularProgress thickness={5} size={22} />
          </StyledLoadingWrapper>
        )}
        {historyQuery.isError && <Alert severity="error">{errorText}</Alert>}
        {historyQuery.isSuccess && historyQuery.data.length === 0 && (
          <StyledEmptyText>{emptyText}</StyledEmptyText>
        )}
        {historyQuery.isSuccess && historyQuery.data.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <StyledHeadCell>
                  <FormattedMessage
                    id="ContractFieldHistory.Fecha"
                    defaultMessage="Fecha"
                  />
                </StyledHeadCell>
                <StyledHeadCell>
                  <FormattedMessage
                    id="ContractFieldHistory.Usuario"
                    defaultMessage="Usuario"
                  />
                </StyledHeadCell>
                <StyledHeadCell>
                  <FormattedMessage
                    id="ContractFieldHistory.Valor anterior"
                    defaultMessage="Valor anterior"
                  />
                </StyledHeadCell>
                <StyledHeadCell>
                  <FormattedMessage
                    id="ContractFieldHistory.Valor nuevo"
                    defaultMessage="Valor nuevo"
                  />
                </StyledHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historyQuery.data.map((item) => (
                <TableRow key={item.id}>
                  <StyledBodyCell>
                    {moment(item.createdAt).format("DD/MM/YYYY HH:mm")}
                  </StyledBodyCell>
                  <StyledBodyCell>{item.userFullname ?? "-"}</StyledBodyCell>
                  <StyledBodyCell>{item.oldValue ?? "-"}</StyledBodyCell>
                  <StyledBodyCell>{item.newValue ?? "-"}</StyledBodyCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          <FormattedMessage
            id="ContractFieldHistory.Cerrar"
            defaultMessage="Cerrar"
          />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContractFieldHistoryDialog;
